/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
import React from "react";

/**
 * CustomKeratometryModal component for displaying keratometry readings
 * @param {Object} props - Component props
 * @param {Function} props.closePopup - Function to close the modal
 * @param {Object} props.biometryData - Biometry data containing keratometry information
 */
const CustomKeratometryModal = ({ closePopup, biometryData }) => {
  const formatKeratometryValue = value => {
    if (value === null || value === undefined || value === 0) {
      return "-";
    }
    return value;
  };

  // Format biometry values with units
  const formatKeratometryK1 = value => {
    if (value === null || value === undefined || value === "") return "-";
    // If it's already a formatted string with mm and DS, return it
    if (
      typeof value === "string" &&
      value.includes("mm") &&
      value.includes("DS")
    ) {
      return value;
    }
    // If it's a string with multiple values, parse it
    if (typeof value === "string") {
      // Parse format like "7.35/45.15D@168°"
      const parts = value.split("/");
      if (parts.length >= 2) {
        const mm = parseFloat(parts[0]);
        const rest = parts[1]; // "45.15D@168°"

        // Extract diopter and angle from "45.15D@168°"
        const match = rest.match(/([\d.]+)D@(\d+)°/);
        if (match && !isNaN(mm)) {
          const ds = parseFloat(match[1]);
          const angle = parseFloat(match[2]);
          if (!isNaN(ds) && !isNaN(angle)) {
            return `${mm.toFixed(2)} mm /${ds.toFixed(2)} DS @${angle}°`;
          }
        }
      }
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num.toFixed(2)} mm`;
      }
      return value;
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} mm`;
  };

  const formatKeratometryK2 = value => {
    return formatKeratometryK1(value);
  };

  const formatKeratometryAST = value => {
    if (value === null || value === undefined || value === "") return "-";
    // If it's already a formatted string with DC and @, return it
    if (
      typeof value === "string" &&
      value.includes("DC") &&
      value.includes("@")
    ) {
      return value;
    }
    // If it's a string with multiple values, parse it
    if (typeof value === "string") {
      // Parse format like "-0.65D@168°"
      const match = value.match(/([\d.-]+)D@(\d+)°/);
      if (match) {
        const dc = parseFloat(match[1]);
        const angle = parseFloat(match[2]);
        if (!isNaN(dc) && !isNaN(angle)) {
          return `${dc.toFixed(2)}DC @${angle}°`;
        }
      }
      // Try to parse format like "-0.65/168" or similar
      const parts = value.split("/");
      if (parts.length >= 2) {
        const dc = parseFloat(parts[0]);
        const angle = parseFloat(parts[1]);
        if (!isNaN(dc) && !isNaN(angle)) {
          return `${dc.toFixed(2)}DC @${angle}°`;
        }
      }
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num.toFixed(2)}DC`;
      }
      return value;
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)}DC`;
  };

  const formatAvgK = value => {
    if (value === null || value === undefined || value === 0) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)} mm`;
  };

  const formatALCR = value => {
    if (value === null || value === undefined || value === 0) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return num.toFixed(2); // No unit
  };

  const CompassVisualization = ({ eyeData, eyeLabel }) => {
    if (!eyeData || !eyeData.keratometryData) {
      return (
        <div className="compass-container">
          <div className="compass-title">{eyeLabel}</div>
          <div className="no-data">No data available</div>
        </div>
      );
    }

    const { ax1, ax2, r1, r2, d1, d2 } = eyeData.keratometryData;

    return (
      <div className="compass-container">
        <div className="compass-title">{eyeLabel}</div>
        <div className="compass-visualization">
          {/* Compass background */}
          <div className="compass-bg">
            <svg width="320" height="320" viewBox="0 0 320 320">
              {/* Outer circle with degree markings */}
              <circle
                cx="160"
                cy="160"
                r="140"
                fill="none"
                stroke="#666"
                strokeWidth="2"
              />
              <circle
                cx="160"
                cy="160"
                r="100"
                fill="none"
                stroke="#666"
                strokeWidth="1"
              />

              {/* Degree markings */}
              {Array.from({ length: 36 }, (_, i) => {
                const angle = i * 10;
                const radian = (angle * Math.PI) / 180;
                const x1 = 160 + 135 * Math.cos(radian);
                const y1 = 160 + 135 * Math.sin(radian);
                const x2 = 160 + 125 * Math.cos(radian);
                const y2 = 160 + 125 * Math.sin(radian);

                return (
                  <g key={angle}>
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="#666"
                      strokeWidth="1"
                    />
                    {angle % 30 === 0 && (
                      <text
                        x={160 + 155 * Math.cos(radian)}
                        y={160 + 155 * Math.sin(radian)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#999"
                        fontSize="12"
                      >
                        {angle}°
                      </text>
                    )}
                  </g>
                );
              })}

              {/* K1 Arrow - Blue */}
              <g transform={`rotate(${ax1} 160 160)`}>
                <line
                  x1="160"
                  y1="160"
                  x2="250"
                  y2="160"
                  stroke="#4A90E2"
                  strokeWidth="3"
                />
                <polygon points="255,160 245,155 245,165" fill="#4A90E2" />
              </g>

              {/* K2 Arrow - Orange */}
              <g transform={`rotate(${ax2} 160 160)`}>
                <line
                  x1="160"
                  y1="160"
                  x2="250"
                  y2="160"
                  stroke="#F5A623"
                  strokeWidth="3"
                />
                <polygon points="255,160 245,155 245,165" fill="#F5A623" />
              </g>

              {/* Center dot */}
              <circle cx="160" cy="160" r="3" fill="#333" />
            </svg>
          </div>

          {/* Legend */}
          <div className="compass-legend">
            <div className="legend-item">
              <span className="legend-color k1-color"></span>
              <span>
                K1: {formatKeratometryValue(r1)}/{formatKeratometryValue(d1)}D@
                {formatKeratometryValue(ax1)}°
              </span>
            </div>
            <div className="legend-item">
              <span className="legend-color k2-color"></span>
              <span>
                K2: {formatKeratometryValue(r2)}/{formatKeratometryValue(d2)}D@
                {formatKeratometryValue(ax2)}°
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="custommodal">
      <div className="custommodalinner keratometry-readings-modal">
        <div className="firstpage">
          <div className="hrpadding">
            <h4 className="modal-heading">Keratometry Readings</h4>
            <button className="cancle" onClick={closePopup}>
              <span className="cross">✕</span>
            </button>
          </div>

          <div className="keratometry-content">
            <div className="keratometry-visualizations">
              <CompassVisualization
                eyeData={biometryData?.rightEye}
                eyeLabel="OD/R"
              />
              <CompassVisualization
                eyeData={biometryData?.leftEye}
                eyeLabel="OS/L"
              />
            </div>

            <div className="keratometry-table-section">
              <h5 className="table-section-title">Detailed Measurements</h5>
              <table className="keratometry-table">
                <thead>
                  <tr>
                    <th>Eye</th>
                    <th>K1</th>
                    <th>K2</th>
                    <th>AST</th>
                    <th>AVG K</th>
                    <th>AL/CR</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="eye-label">OD/R</td>
                    <td>
                      {formatKeratometryK1(
                        biometryData?.rightEye?.keratometryData?.k1
                      )}
                    </td>
                    <td>
                      {formatKeratometryK2(
                        biometryData?.rightEye?.keratometryData?.k2
                      )}
                    </td>
                    <td>
                      {formatKeratometryAST(
                        biometryData?.rightEye?.keratometryData?.ast
                      )}
                    </td>
                    <td>
                      {formatAvgK(
                        biometryData?.rightEye?.keratometryData?.avgR
                      )}
                    </td>
                    <td>{formatALCR(biometryData?.rightEye?.alCr)}</td>
                  </tr>
                  <tr>
                    <td className="eye-label">OS/L</td>
                    <td>
                      {formatKeratometryK1(
                        biometryData?.leftEye?.keratometryData?.k1
                      )}
                    </td>
                    <td>
                      {formatKeratometryK2(
                        biometryData?.leftEye?.keratometryData?.k2
                      )}
                    </td>
                    <td>
                      {formatKeratometryAST(
                        biometryData?.leftEye?.keratometryData?.ast
                      )}
                    </td>
                    <td>
                      {formatAvgK(biometryData?.leftEye?.keratometryData?.avgR)}
                    </td>
                    <td>{formatALCR(biometryData?.leftEye?.alCr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomKeratometryModal;
